(() => {
  'use strict';

  const STORAGE_KEY = 'styleomg_locale_manual_v1';
  const locales = [
    { code: 'en', path: '', label: 'English' },
    { code: 'it', path: 'it', label: 'Italiano' },
    { code: 'fr', path: 'fr', label: 'Français' },
    { code: 'de', path: 'de', label: 'Deutsch' },
    { code: 'es', path: 'es', label: 'Español' },
    { code: 'nl', path: 'nl', label: 'Nederlands' },
    { code: 'pt-PT', path: 'pt', label: 'Português' },
    { code: 'sv', path: 'sv', label: 'Svenska' },
    { code: 'da', path: 'da', label: 'Dansk' }
  ];
  const pathToCode = Object.fromEntries(locales.filter(x => x.path).map(x => [x.path, x.code]));
  const localizedPrefix = /^\/(it|fr|de|es|nl|pt|sv|da)(?=\/|$)/i;

  const canonical = (value) => {
    const v = String(value || 'en').trim().toLowerCase().replace('_', '-');
    if (v === 'pt' || v === 'pt-pt') return 'pt-PT';
    const short = v.split('-')[0];
    return locales.some(x => x.code.toLowerCase() === v || x.code.toLowerCase() === short) ?
      (locales.find(x => x.code.toLowerCase() === v || x.code.toLowerCase() === short)?.code || 'en') : 'en';
  };

  const currentLocale = () => {
    const match = location.pathname.match(localizedPrefix);
    if (match) return pathToCode[match[1].toLowerCase()] || 'en';
    return canonical(document.documentElement.lang || 'en');
  };

  const remember = (code) => {
    const value = canonical(code);
    if (window.StyleOmgLocale?.saveManual) { window.StyleOmgLocale.saveManual(value); return; }
    try {
      localStorage.setItem(STORAGE_KEY, value);
      localStorage.setItem('styleomg_locale_initial_routed_v1', '1');
    } catch (_) {}
  };

  const buildUrl = (code) => {
    const target = locales.find(x => x.code === canonical(code)) || locales[0];
    const url = new URL(location.href);
    let base = url.pathname.replace(localizedPrefix, '');
    if (!base) base = '/';
    if (!base.startsWith('/')) base = '/' + base;
    url.pathname = target.path ? `/${target.path}${base === '/' ? '/' : base}` : base;
    return url.toString();
  };

  const submitNativeLocalization = (code) => {
    const target = canonical(code);
    const form =
      document.querySelector('#FooterLanguageForm') ||
      document.querySelector('form[action*="/localization"] [name="locale_code"]')?.form;

    if (!form) return false;

    const localeInput = form.querySelector('[name="locale_code"]');
    if (!localeInput) return false;

    localeInput.value = target;

    const country =
      document.querySelector('#FooterCountryForm [name="country_code"]')?.value ||
      document.querySelector('form[action*="/localization"] [name="country_code"]')?.value ||
      window.Shopify?.country ||
      '';

    if (country && !form.querySelector('[name="country_code"]')) {
      const countryInput = document.createElement('input');
      countryInput.type = 'hidden';
      countryInput.name = 'country_code';
      countryInput.value = String(country).toUpperCase();
      countryInput.dataset.styleOmgTemporaryCountry = '1';
      form.appendChild(countryInput);
    }

    try {
      form.submit();
      return true;
    } catch (_) {
      return false;
    }
  };

  const navigate = (code) => {
    const target = canonical(code);
    remember(target);
    if (target === currentLocale()) return;

    // Use Shopify's native localization endpoint first. This preserves the
    // active country/Market context (for example Canada + CAD) while changing
    // only the language. URL rewriting remains only as a safe fallback.
    if (submitNativeLocalization(target)) return;

    const next = buildUrl(target);
    if (next !== location.href) location.assign(next);
  };

  const labels = {
    en: 'Language', it: 'Lingua', fr: 'Langue', de: 'Sprache', es: 'Idioma',
    nl: 'Taal', 'pt-PT': 'Idioma', sv: 'Språk', da: 'Sprog'
  };

  const bindNative = () => {
    document.querySelectorAll('form[action*="/localization"]').forEach(form => {
      if (form.dataset.styleOmgLocaleBound === '1') return;
      form.dataset.styleOmgLocaleBound = '1';
      form.addEventListener('submit', () => {
        const input = form.querySelector('[name="locale_code"]');
        if (input?.value) remember(input.value);
      }, true);
    });
    document.querySelectorAll('#FooterLanguageForm [data-value], #FooterLanguageFormNoScript option').forEach(el => {
      if (el.dataset.styleOmgLocaleBound === '1') return;
      el.dataset.styleOmgLocaleBound = '1';
      el.addEventListener('click', () => {
        const value = el.dataset.value || el.value;
        if (value) remember(value);
      }, true);
    });
  };

  // ---------------------------------------------------------------------------
  // STYLEOMG MARKET-CURRENCY HOTFIX
  // Scope: only custom upsell prices after a customer changes a variant.
  // Initial Liquid-rendered prices are already correct, so no other price/UI
  // behavior is touched.
  // ---------------------------------------------------------------------------
  const resolveActiveCurrency = () => {
    const direct = String(window.Shopify?.currency?.active || '').trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(direct)) return direct;

    const dataCurrency = String(
      document.querySelector('[data-omg-standalone-price][data-currency]')?.dataset.currency || ''
    ).trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(dataCurrency)) return dataCurrency;

    const countryButtonText =
      document.querySelector('#FooterCountryForm button')?.textContent ||
      document.querySelector('#FooterCountryFormNoScript option:checked')?.textContent ||
      '';
    const countryCurrency = String(countryButtonText).match(/\(([A-Z]{3})\b/);
    if (countryCurrency) return countryCurrency[1].toUpperCase();

    return 'USD';
  };

  const formatActiveMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    const currency = resolveActiveCurrency();
    const locale = document.documentElement.lang || currentLocale() || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(value);
    } catch (_) {
      const digits = currency === 'VND' ? 0 : 2;
      return `${currency} ${value.toFixed(digits)}`;
    }
  };

  const parseVariants = (script) => {
    if (!script) return [];
    try {
      const variants = JSON.parse(script.textContent || script.innerHTML || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (_) {
      return [];
    }
  };

  const findSelectedVariant = (card, selector, indexAttribute, variants) => {
    if (!card || !variants.length) return null;

    const selected = {};
    card.querySelectorAll(selector).forEach((select) => {
      const key = select.getAttribute(indexAttribute);
      if (key) selected[key] = select.value;
    });

    return variants.find((variant) => {
      return (!selected.option1 || variant.option1 === selected.option1) &&
        (!selected.option2 || variant.option2 === selected.option2) &&
        (!selected.option3 || variant.option3 === selected.option3);
    }) || null;
  };

  const fixRelatedListUpsellPrice = (select) => {
    const cardId = select.getAttribute('data-card-id');
    if (!cardId) return;

    const card = document.getElementById(`rs-card-${cardId}`);
    const variants = parseVariants(document.getElementById(`rs-variant-data-${cardId}`));
    const variant = findSelectedVariant(card, '.rs-option-selector', 'data-index', variants);
    if (!card || !variant) return;

    const price = document.getElementById(`rs-price-${cardId}`);
    const compare = document.getElementById(`rs-compare-${cardId}`);

    if (price) price.textContent = formatActiveMoney(variant.price);

    if (compare) {
      if (Number(variant.compare_at_price || 0) > Number(variant.price || 0)) {
        compare.textContent = formatActiveMoney(variant.compare_at_price);
        compare.style.display = 'block';
      } else {
        compare.style.display = 'none';
      }
    }
  };

  const fixCartUpsellPrice = (select) => {
    const cardId = select.getAttribute('data-card-id');
    if (!cardId) return;

    const card = document.getElementById(`cu-card-${cardId}`);
    const variants = parseVariants(document.getElementById(`cu-variant-data-${cardId}`));
    const variant = findSelectedVariant(card, '.cu-option-selector', 'data-index', variants);
    if (!card || !variant) return;

    const price = document.getElementById(`cu-price-${cardId}`);
    if (price) price.textContent = formatActiveMoney(variant.price);
  };

  const fixStandardUpsellPrice = (select) => {
    const card = select.closest('.so-upsell__card');
    if (!card) return;

    const variants = parseVariants(card.querySelector('[data-upsell-variants]'));
    const variant = findSelectedVariant(card, '[data-option-index]', 'data-option-index', variants);
    if (!variant) return;

    const price = card.querySelector('[data-upsell-price]');
    const compare = card.querySelector('[data-upsell-compare]');

    if (price) price.textContent = formatActiveMoney(variant.price);

    if (compare) {
      if (Number(variant.compare_at_price || 0) > Number(variant.price || 0)) {
        compare.textContent = formatActiveMoney(variant.compare_at_price);
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
  };

  const bindDynamicPriceCurrencyFix = () => {
    if (document.documentElement.dataset.styleOmgCurrencyFixBound === '1') return;
    document.documentElement.dataset.styleOmgCurrencyFixBound = '1';

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const isRelated = target.matches('.rs-option-selector');
      const isCartUpsell = target.matches('.cu-option-selector');
      const isStandardUpsell = target.matches('.so-upsell__select[data-option-index], [data-option-index].so-upsell__select');

      if (!isRelated && !isCartUpsell && !isStandardUpsell) return;

      // Run after each block's own change handler. This changes price text only;
      // variant IDs, image changes, availability and add-to-cart logic stay intact.
      requestAnimationFrame(() => {
        if (isRelated) fixRelatedListUpsellPrice(target);
        else if (isCartUpsell) fixCartUpsellPrice(target);
        else fixStandardUpsellPrice(target);
      });
    }, false);
  };

  const mount = () => {
    if (document.querySelector('[data-styleomg-language-switcher="v3"]')) return;
    const footer = document.querySelector('.footer__localization') || document.querySelector('footer .footer__content-bottom-wrapper') || document.querySelector('footer');
    if (!footer) return;

    const active = currentLocale();
    const wrap = document.createElement('div');
    wrap.className = 'styleomg-language-switcher';
    wrap.dataset.styleomgLanguageSwitcher = 'v3';

    const label = document.createElement('label');
    label.className = 'styleomg-language-switcher__label';
    label.htmlFor = 'StyleOmgLanguageSelect';
    label.textContent = labels[active] || labels.en;

    const select = document.createElement('select');
    select.id = 'StyleOmgLanguageSelect';
    select.className = 'styleomg-language-switcher__select';
    select.setAttribute('aria-label', labels[active] || labels.en);
    locales.forEach(locale => {
      const option = document.createElement('option');
      option.value = locale.code;
      option.textContent = locale.label;
      option.lang = locale.code;
      option.selected = locale.code === active;
      select.appendChild(option);
    });
    select.addEventListener('change', () => navigate(select.value));

    wrap.append(label, select);
    footer.appendChild(wrap);

    const native = document.querySelector('#FooterLanguageForm')?.closest('localization-form');
    if (native) native.hidden = true;

    if (!document.getElementById('StyleOmgLanguageSwitcherStyle')) {
      const style = document.createElement('style');
      style.id = 'StyleOmgLanguageSwitcherStyle';
      style.textContent = `
        .styleomg-language-switcher{display:flex;align-items:center;gap:.8rem;flex-wrap:wrap;margin:0;min-height:4.4rem}
        .styleomg-language-switcher__label{font-size:1.2rem;line-height:1.4;opacity:.82;font-weight:400}
        .styleomg-language-switcher__select{appearance:auto;min-width:14rem;max-width:100%;height:4rem;padding:0 1rem;border:1px solid rgba(var(--color-foreground),.28);border-radius:.4rem;background:rgb(var(--color-background));color:rgb(var(--color-foreground));font:inherit;font-weight:400;cursor:pointer}
        .styleomg-language-switcher__select:focus{outline:2px solid rgba(var(--color-foreground),.55);outline-offset:2px}
        @media(max-width:749px){.styleomg-language-switcher{width:100%;justify-content:center}.styleomg-language-switcher__select{min-width:16rem}}
      `;
      document.head.appendChild(style);
    }
  };

  const boot = () => {
    bindNative();
    bindDynamicPriceCurrencyFix();
    mount();
    new MutationObserver(() => { bindNative(); mount(); }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
